import { useState, useRef, useEffect } from 'react';
import { User, LogOut, Shield, ChevronDown, Eye } from 'lucide-react';
import { useAuth0 } from '@auth0/auth0-react';
import { useAdminStatus } from '../hooks/useAdminStatus';
import { useFinanceStatus } from '../hooks/useFinanceStatus';
import { useLogout } from './AuthGuard';

interface UserProfileProps {
  onNavigateToProfile: () => void;
}

export default function UserProfile({ onNavigateToProfile }: UserProfileProps) {
  const { user } = useAuth0();
  const { admin } = useAdminStatus();
  const { financeUser } = useFinanceStatus();
  const logout = useLogout();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Get the current user data (admin or finance)
  const currentUser = admin || financeUser;
  const userType = admin ? 'Admin' : 'Finance';

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = () => {
    setIsOpen(false);
    logout();
  };

  if (!user || !currentUser) {
    return null;
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-3 p-2 rounded-xl hover:bg-slate-100/10 dark:hover:bg-slate-800/50 transition-all duration-200 group"
      >
        <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center shadow-lg">
          {user.picture ? (
            <img 
              src={user.picture} 
              alt={`${currentUser.firstname} ${currentUser.lastname}`} 
              className="w-8 h-8 rounded-lg object-cover"
            />
          ) : (
            <User className="w-4 h-4 text-white" />
          )}
        </div>
        <div className="text-left hidden sm:block">
          <div className="text-sm font-medium text-slate-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
            {currentUser.firstname} {currentUser.lastname}
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-400">
            {userType}
          </div>
        </div>
        <ChevronDown className={`w-4 h-4 text-slate-500 dark:text-slate-400 transition-transform duration-200 ${
          isOpen ? 'rotate-180' : ''
        }`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-72 bg-white/95 dark:bg-slate-800/95 backdrop-blur-xl rounded-xl shadow-xl border border-slate-200/60 dark:border-slate-700/60 z-50 overflow-hidden">
          {/* User Info */}
          <div className="px-4 py-3 border-b border-slate-200/60 dark:border-slate-700/60">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center shadow-lg flex-shrink-0">
                <User className="w-5 h-5 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-medium text-slate-900 dark:text-white truncate">
                  {currentUser.firstname} {currentUser.lastname}
                </div>
                <div className="text-sm text-slate-500 dark:text-slate-400 truncate">
                  {currentUser.email}
                </div>
                <div className="flex items-center space-x-1 mt-1">
                  <Shield className="w-3 h-3 text-blue-500 flex-shrink-0" />
                  <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">
                    {userType} User
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Menu Items */}
          <div>
            <button
              onClick={() => {
                setIsOpen(false);
                onNavigateToProfile();
              }}
              className="w-full flex items-center space-x-3 px-4 py-3 text-left hover:bg-slate-100/80 dark:hover:bg-slate-700/50 transition-colors group"
            >
              <Eye className="w-4 h-4 text-slate-500 dark:text-slate-400 group-hover:text-blue-500" />
              <span className="text-slate-700 dark:text-slate-300 group-hover:text-blue-600 dark:group-hover:text-blue-400">
                View Profile
              </span>
            </button>
            
            <button
              onClick={handleLogout}
              className="w-full flex items-center space-x-3 px-4 py-3 text-left hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors group"
            >
              <LogOut className="w-4 h-4 text-slate-500 dark:text-slate-400 group-hover:text-red-500" />
              <span className="text-slate-700 dark:text-slate-300 group-hover:text-red-600 dark:group-hover:text-red-400">
                Sign Out
              </span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}